// The three print documents, for any Genie, with nothing to pick and nothing
// to fill in.
//
// Every Genie gets exactly the same three. There is no per-Genie configuration
// and no stored artwork: a document is built in the browser the moment it is
// asked for, off the Genie's live record. A renamed Genie or a changed URL can
// therefore never leave a stale PDF on a shelf pointing at a dead page.
//
//   Pack      the curated day-one file — posters, sticker sheets, clings,
//             a table tent, pocket cards and counter strips
//   Posters   every poster design, grouped by where it goes
//   Stickers  every die-cut sheet, plus how to apply them
//
// The builders under `./` are browser-side twins of the ones in
// Help-Genie-Operations (`src/pages/ActivationPacks/document/`), so a pack
// printed by a customer here and one printed by the team there are the same
// document. Keep them in step.

import { ActivationPackService } from '@/services/database/ActivationPackService';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import {
  inferPackCapability,
  resolvePackKit,
  type PackGenieOption,
  type PackKit,
} from '@/types/activation/packKit';
import { resolvePrintBrand } from './printBrand';
import { ALL_SELECTED, buildPackHtml } from './buildPackHtml';
import {
  buildPosterBookHtml,
  buildStickerBookHtml,
  type BuiltDocument,
} from './buildBookHtml';

export type PackDocumentId = 'pack' | 'posters' | 'stickers';

export interface PackDocumentMeta {
  id: PackDocumentId;
  /** The name on the card and on the printed cover. */
  title: string;
  /** One line of what is inside, for the card. */
  blurb: string;
  /** Roughly what comes out of the printer, so nobody is surprised by the job. */
  contains: string;
}

/** The three, in the order they are shown. Same set for every Genie. */
export const PACK_DOCUMENTS: PackDocumentMeta[] = [
  {
    id: 'pack',
    title: 'Activation pack',
    blurb:
      'The day-one file. Print this one and your Genie is on the door, the counter and the van by Friday.',
    contains: 'Posters, four sticker sheets, window clings, a table tent, pocket cards, counter strips',
  },
  {
    id: 'posters',
    title: 'Poster book',
    blurb:
      'Every poster design, grouped by where it goes, plus a page on hanging them so they actually get scanned.',
    contains: 'Every poster design, in sections, with a hanging guide',
  },
  {
    id: 'stickers',
    title: 'Sticker book',
    blurb:
      'Every sticker, label, cling and magnet sheet, plus the four steps that decide whether one lasts a season or a week.',
    contains: 'Every die-cut sheet, with an application guide',
  },
];

/**
 * The live public page every code in every document points at.
 *
 * Null when the Genie has no published page. `getTargetUrl` falls back to the
 * site root in that case, and a wall of QR codes pointing at a marketing
 * homepage is the one failure that cannot be recalled once it is printed — so
 * the caller shows nothing rather than a pack that scans wrong.
 */
export const resolvePackPrintUrl = (pack: ActivationPackWithTarget): string | null => {
  const hasPublicPage = Boolean(
    pack.agent?.url_name || pack.agent?.page?.url_name || pack.portal?.slug,
  );
  if (!hasPublicPage) return null;
  return ActivationPackService.getTargetUrl(pack);
};

/**
 * The kit behind every printed piece.
 *
 * Three layers, in this order:
 *
 *   the Genie          name, destination, and the capability its text implies
 *   its capability     the starter words, so an untouched Genie still prints
 *   `pack.print_kit`   whatever the customer has edited, on top
 *
 * REVISED 2026-08-09. This used to pass `null` as the saved layer and the
 * comment above it read "Nothing here is typed by the customer, which is the
 * point — the three documents are standard." Standard is what made a PrintIQ
 * pack print the same four generic bullets as everyone else's: the capability
 * keyword match falls through to `faq` for most names, and `faq` is deliberately
 * about-nothing copy. The layers were already built for this; only the read
 * was missing.
 *
 * The brand is resolved alongside, from the Genie's own branding. It rides on
 * the kit because every page builder already takes one.
 */
export const resolvePackPrintKit = (pack: ActivationPackWithTarget): PackKit | null => {
  const url = resolvePackPrintUrl(pack);
  if (!url) return null;

  const name = ActivationPackService.getTargetName(pack);
  const option: PackGenieOption = {
    key: pack.agent_id ? `agent:${pack.agent_id}` : `portal:${pack.portal_id}`,
    name,
    publicUrl: url,
    capability: inferPackCapability([name, pack.name, pack.description]),
  };

  const branding = ActivationPackService.getEffectiveBranding(pack);
  // The name under the logo is the brand's, not the Genie's: a poster on
  // PrintIQ's door says "PrintIQ", not "PrintIQ Project Genie". Where no brand
  // name has been set, the Genie's own name is the only name we have.
  const brand = resolvePrintBrand(branding, branding.brandName?.trim() || name);

  return resolvePackKit(option, pack.print_kit ?? null, brand);
};

/** The kit as the capability alone would fill it. What "reset" restores. */
export const resolvePackPresetKit = (pack: ActivationPackWithTarget): PackKit | null => {
  const url = resolvePackPrintUrl(pack);
  if (!url) return null;
  return resolvePackPrintKit({ ...pack, print_kit: null });
};

/** Build one of the three. Pure function of the kit — no storage, no cache. */
export const buildPackDocument = (
  id: PackDocumentId,
  kit: PackKit,
): Promise<BuiltDocument> => {
  if (id === 'posters') return buildPosterBookHtml(kit);
  if (id === 'stickers') return buildStickerBookHtml(kit);
  return buildPackHtml(kit, ALL_SELECTED);
};

export type { BuiltDocument };
